import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API_BASE from "../config";

const ReportDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const type = location.state?.type || "";

    const [bookings, setBookings] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const perPage = 12;

    // Page meta per type
    const pageMeta = {
        booked_revenue: { title: "Total Booked Revenue", icon: "💰", desc: "All bookings in the system" },
        confirmed_sale: { title: "Confirmed Sale Amount", icon: "✅", desc: "Bookings where registry is done (r_status = Done)" },
        total_received: { title: "Total Received", icon: "💵", desc: "All payments with status Confirmed" },
        total_balance: { title: "Total Balance", icon: "📊", desc: "Remaining balance per booking (Sale Price + GST − Confirmed Payments)" },
        self_received: { title: "Self Received", icon: "🧑", desc: "Payments where payment_part = Self" },
        self_balance: { title: "Self Balance", icon: "📋", desc: "Remaining self pay balance per booking" },
        bank_received: { title: "Bank Received", icon: "🏦", desc: "Payments where payment_part = Bank Loan" },
        bank_balance: { title: "Bank Balance", icon: "🏦", desc: "Remaining bank loan balance per booking" },
    };
    const meta = pageMeta[type] || { title: "Report Detail", icon: "📄", desc: "" };

    useEffect(() => {
        if (!type) { navigate("/dashboard/reports"); return; }
        fetchData();
        // eslint-disable-next-line
    }, [type]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Always fetch all bookings
            const bkRes = await fetch(`${API_BASE}/api/allrows`, {
                method: "POST",
                headers: { Authorization: "1", "Content-Type": "application/json" },
                body: JSON.stringify({ token: "3232", table: "bookings" }),
            });
            const bkJson = await bkRes.json();
            if (Array.isArray(bkJson.data)) setBookings(bkJson.data);

            // Fetch all payments (for received / balance calculations)
            const pyRes = await fetch(`${API_BASE}/api/allrows`, {
                method: "POST",
                headers: { Authorization: "1", "Content-Type": "application/json" },
                body: JSON.stringify({ token: "3232", table: "payments" }),
            });
            const pyJson = await pyRes.json();
            if (Array.isArray(pyJson.data)) setPayments(pyJson.data);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    const fmt = (v) => {
        const n = Number(v);
        return isNaN(n) || n === 0 ? "—" : "₹" + n.toLocaleString("en-IN");
    };

    // ========== Build rows based on type ==========
    let tableHeaders = [];
    let tableRows = [];
    let summaryAmount = 0;

    const searchFilter = (obj) => Object.values(obj).join(" ").toLowerCase().includes(search.toLowerCase());

    if (type === "booked_revenue") {
        // All bookings
        const filtered = bookings.filter(searchFilter);
        tableHeaders = ["Customer", "Unit", "Project", "Sale Price", "GST", "Total", "Payment Source", "Date", "Action"];
        tableRows = filtered;
        summaryAmount = filtered.reduce((s, b) => s + Number(b.total_saleprice || 0), 0);
    }

    if (type === "confirmed_sale") {
        // Bookings where r_status = "Done"
        const filtered = bookings.filter(b => b.r_status === "Done").filter(searchFilter);
        tableHeaders = ["Customer", "Unit", "Project", "Sale Price", "GST", "Total", "Registry", "Date", "Action"];
        tableRows = filtered;
        summaryAmount = filtered.reduce((s, b) => s + Number(b.total_saleprice || 0), 0);
    }

    if (type === "total_received") {
        // Payments where status = "Confirmed"
        const filtered = payments.filter(p => p.status === "Confirmed").filter(searchFilter);
        tableHeaders = ["Booking ID", "Customer", "Amount", "Payment Part", "Payment For", "Date", "Mode", "Status"];
        tableRows = filtered;
        summaryAmount = filtered.reduce((s, p) => s + Number(p.amount || 0), 0);
    }

    if (type === "total_balance") {
        // For each booking: booking_amount = total_saleprice + sale_gst
        // Sum confirmed payments matching booking_id
        // Balance = booking_amount − total_payments
        const confirmedPayments = payments.filter(p => p.status === "Confirmed");
        const paymentsByBooking = {};
        confirmedPayments.forEach(p => {
            const bid = p.booking_id;
            if (!bid) return;
            paymentsByBooking[bid] = (paymentsByBooking[bid] || 0) + Number(p.amount || 0);
        });

        const rows = bookings.map(b => {
            const bookingAmt = Number(b.total_saleprice || 0) + Number(b.sale_gst || 0);
            const paid = paymentsByBooking[b.booking_id] || 0;
            const balance = bookingAmt - paid;
            return { ...b, _bookingAmt: bookingAmt, _paid: paid, _balance: balance };
        }).filter(searchFilter);

        tableHeaders = ["Customer", "Unit", "Project", "Booking Amt", "Paid", "Balance", "Date", "Action"];
        tableRows = rows;
        summaryAmount = rows.reduce((s, r) => s + r._balance, 0);
    }

    if (type === "self_received") {
        // Payments where payment_part = "Self"
        const filtered = payments.filter(p => p.payment_part === "Self").filter(searchFilter);
        tableHeaders = ["Booking ID", "Customer", "Amount", "Payment For", "Date", "Mode", "Status"];
        tableRows = filtered;
        summaryAmount = filtered.reduce((s, p) => s + Number(p.amount || 0), 0);
    }

    if (type === "self_balance") {
        // Booking self_pay − sum of confirmed payments where payment_part = "Self" for that booking
        const selfPayments = payments.filter(p => p.payment_part === "Self" && p.status === "Confirmed");
        const selfByBooking = {};
        selfPayments.forEach(p => {
            const bid = p.booking_id;
            if (!bid) return;
            selfByBooking[bid] = (selfByBooking[bid] || 0) + Number(p.amount || 0);
        });

        const rows = bookings.map(b => {
            const selfTarget = Number(b.self_pay || 0);
            const selfPaid = selfByBooking[b.booking_id] || 0;
            const balance = selfTarget - selfPaid;
            return { ...b, _selfTarget: selfTarget, _selfPaid: selfPaid, _balance: balance };
        }).filter(r => r._selfTarget > 0).filter(searchFilter);

        tableHeaders = ["Customer", "Unit", "Project", "Self Pay", "Received", "Balance", "Date", "Action"];
        tableRows = rows;
        summaryAmount = rows.reduce((s, r) => s + r._balance, 0);
    }

    if (type === "bank_received") {
        // Payments where payment_part = "Bank Loan"
        const filtered = payments.filter(p => p.payment_part === "Bank Loan").filter(searchFilter);
        tableHeaders = ["Booking ID", "Customer", "Amount", "Payment For", "Date", "Mode", "Status"];
        tableRows = filtered;
        summaryAmount = filtered.reduce((s, p) => s + Number(p.amount || 0), 0);
    }

    if (type === "bank_balance") {
        // Booking loan_amount − sum of confirmed payments where payment_part = "Bank Loan" for that booking
        const bankPayments = payments.filter(p => p.payment_part === "Bank Loan" && p.status === "Confirmed");
        const bankByBooking = {};
        bankPayments.forEach(p => {
            const bid = p.booking_id;
            if (!bid) return;
            bankByBooking[bid] = (bankByBooking[bid] || 0) + Number(p.amount || 0);
        });

        const rows = bookings.map(b => {
            const loanTarget = Number(b.loan_amount || 0);
            const bankPaid = bankByBooking[b.booking_id] || 0;
            const balance = loanTarget - bankPaid;
            return { ...b, _loanTarget: loanTarget, _bankPaid: bankPaid, _balance: balance };
        }).filter(r => r._loanTarget > 0).filter(searchFilter);

        tableHeaders = ["Customer", "Unit", "Project", "Loan Amount", "Received", "Balance", "Date", "Action"];
        tableRows = rows;
        summaryAmount = rows.reduce((s, r) => s + r._balance, 0);
    }

    // Pagination
    const totalPages = Math.ceil(tableRows.length / perPage);
    const paginatedRows = tableRows.slice((page - 1) * perPage, page * perPage);

    // ========== Render table cell per type ==========
    const renderRow = (row, i) => {
        // Payment-type rows (total_received, self_received, bank_received)
        if (type === "total_received" || type === "self_received" || type === "bank_received") {
            return (
                <tr key={row.id || i}>
                    <td><span className="rd-booking-id">{row.booking_id || "—"}</span></td>
                    <td><span className="rd-customer">{row.customer_name || row.name || "—"}</span></td>
                    <td><span className="rd-amount">{fmt(row.amount)}</span></td>
                    {type === "total_received" && <td>{row.payment_part || "—"}</td>}
                    <td>{row.payment_for || "—"}</td>
                    <td><span className="rd-date">{row.payment_collect || row.date || "—"}</span></td>
                    <td>{row.payment_mode || "—"}</td>
                    <td>
                        <span className={`rd-badge ${row.status === "Confirmed" ? "rd-badge-ok" : "rd-badge-pend"}`}>
                            {row.status || "—"}
                        </span>
                    </td>
                </tr>
            );
        }

        // Balance-type rows
        if (type === "total_balance") {
            return (
                <tr key={row.id || i}>
                    <td><span className="rd-customer">{row.customer_name || "—"}</span></td>
                    <td><span className="rd-unit">{row.unit || "—"}</span></td>
                    <td>{row.project_name || "—"}</td>
                    <td><span className="rd-amount">{fmt(row._bookingAmt)}</span></td>
                    <td><span style={{ color: "#059669", fontWeight: 700 }}>{fmt(row._paid)}</span></td>
                    <td><span style={{ color: row._balance > 0 ? "#dc2626" : "#059669", fontWeight: 700 }}>{fmt(row._balance)}</span></td>
                    <td><span className="rd-date">{row.date || "—"}</span></td>
                    <td>
                        <button className="rd-view-btn" onClick={() => navigate("/dashboard/bookings/view", { state: { booking: row } })}>
                            👁️ View More
                        </button>
                    </td>
                </tr>
            );
        }

        if (type === "self_balance") {
            return (
                <tr key={row.id || i}>
                    <td><span className="rd-customer">{row.customer_name || "—"}</span></td>
                    <td><span className="rd-unit">{row.unit || "—"}</span></td>
                    <td>{row.project_name || "—"}</td>
                    <td><span className="rd-amount">{fmt(row._selfTarget)}</span></td>
                    <td><span style={{ color: "#059669", fontWeight: 700 }}>{fmt(row._selfPaid)}</span></td>
                    <td><span style={{ color: row._balance > 0 ? "#dc2626" : "#059669", fontWeight: 700 }}>{fmt(row._balance)}</span></td>
                    <td><span className="rd-date">{row.date || "—"}</span></td>
                    <td>
                        <button className="rd-view-btn" onClick={() => navigate("/dashboard/bookings/view", { state: { booking: row } })}>
                            👁️ View More
                        </button>
                    </td>
                </tr>
            );
        }

        if (type === "bank_balance") {
            return (
                <tr key={row.id || i}>
                    <td><span className="rd-customer">{row.customer_name || "—"}</span></td>
                    <td><span className="rd-unit">{row.unit || "—"}</span></td>
                    <td>{row.project_name || "—"}</td>
                    <td><span className="rd-amount">{fmt(row._loanTarget)}</span></td>
                    <td><span style={{ color: "#059669", fontWeight: 700 }}>{fmt(row._bankPaid)}</span></td>
                    <td><span style={{ color: row._balance > 0 ? "#dc2626" : "#059669", fontWeight: 700 }}>{fmt(row._balance)}</span></td>
                    <td><span className="rd-date">{row.date || "—"}</span></td>
                    <td>
                        <button className="rd-view-btn" onClick={() => navigate("/dashboard/bookings/view", { state: { booking: row } })}>
                            👁️ View More
                        </button>
                    </td>
                </tr>
            );
        }

        // Booking-type rows (booked_revenue, confirmed_sale)
        return (
            <tr key={row.id || i}>
                <td>
                    <div className="rd-customer">{row.customer_name || "—"}</div>
                    <div className="rd-phone">{row.cust_phone || ""}</div>
                </td>
                <td><span className="rd-unit">{row.unit || "—"}{row.unit2 ? ` + ${row.unit2}` : ""}</span></td>
                <td>{row.project_name || "—"}</td>
                <td><span className="rd-amount">{fmt(row.sale_price || row.total_saleprice)}</span></td>
                <td>{fmt(row.sale_gst)}</td>
                <td><span className="rd-amount">{fmt(row.total_saleprice)}</span></td>
                <td>
                    {type === "confirmed_sale" ? (
                        <span className="rd-badge rd-badge-ok">{row.r_status || "—"}</span>
                    ) : (
                        <span className={`rd-badge ${row.payment_source === "100% Self" ? "rd-badge-ok" : "rd-badge-pend"}`}>
                            {row.payment_source || "—"}
                        </span>
                    )}
                </td>
                <td><span className="rd-date">{row.date || "—"}</span></td>
                <td>
                    <button className="rd-view-btn" onClick={() => navigate("/dashboard/bookings/view", { state: { booking: row } })}>
                        👁️ View More
                    </button>
                </td>
            </tr>
        );
    };

    return (
        <>
            <style>{`
        .rd-page { display:flex; flex-direction:column; gap:20px; }

        .rd-hdr { display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
        .rd-back { padding:8px 16px; background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; color:#334155; cursor:pointer; font-size:14px; font-weight:500; font-family:'Inter',sans-serif; transition:all .2s; display:flex; align-items:center; gap:6px; }
        .rd-back:hover { background:#f1f5f9; border-color:#cbd5e1; }
        .rd-title { font-size:22px; font-weight:700; color:#0f172a; }
        .rd-desc { font-size:13px; color:#64748b; margin-top:2px; }

        .rd-summary { display:flex; gap:16px; flex-wrap:wrap; }
        .rd-summary-card { background:#fff; border:1px solid #e2e8f0; border-radius:14px; padding:18px 24px; min-width:180px; box-shadow:0 1px 3px rgba(0,0,0,.04); }
        .rd-summary-val { font-size:24px; font-weight:800; color:#0f172a; }
        .rd-summary-lbl { font-size:12px; color:#64748b; font-weight:600; text-transform:uppercase; letter-spacing:.4px; margin-top:4px; }
        .rd-summary-card.highlight { background:linear-gradient(135deg,#eff6ff,#eef2ff); border-color:#c7d2fe; }
        .rd-summary-card.highlight .rd-summary-val { color:#4f46e5; }

        .rd-search { background:#fff; padding:14px 16px; border-radius:14px; border:1px solid #e2e8f0; box-shadow:0 1px 3px rgba(0,0,0,.04); }
        .rd-search input { width:100%; padding:10px 14px; border-radius:10px; border:1px solid #e2e8f0; outline:none; font-size:13px; font-family:'Inter',sans-serif; color:#334155; background:#f8fafc; transition:all .2s; }
        .rd-search input:focus { border-color:#818cf8; box-shadow:0 0 0 3px rgba(99,102,241,.1); background:#fff; }

        .rd-wrap { background:#fff; border-radius:14px; border:1px solid #e2e8f0; box-shadow:0 1px 3px rgba(0,0,0,.04); overflow-x:auto; }
        .rd-table { width:100%; border-collapse:collapse; min-width:900px; }
        .rd-table th,.rd-table td { padding:13px 14px; font-size:13px; text-align:left; border-bottom:1px solid #f1f5f9; }
        .rd-table th { background:#f8fafc; color:#64748b; font-weight:600; font-size:11px; text-transform:uppercase; letter-spacing:.5px; white-space:nowrap; position:sticky; top:0; }
        .rd-table tbody tr { transition:background .15s; }
        .rd-table tbody tr:hover { background:#f8fafc; }
        .rd-table td { color:#334155; font-weight:500; }

        .rd-customer { font-weight:600; color:#0f172a; text-transform:capitalize; }
        .rd-phone { font-size:11px; color:#94a3b8; }
        .rd-unit { font-weight:600; color:#2563eb; }
        .rd-amount { font-weight:700; color:#0f172a; font-variant-numeric:tabular-nums; }
        .rd-date { font-size:12px; color:#64748b; white-space:nowrap; }
        .rd-booking-id { font-weight:600; color:#6366f1; font-size:12px; }

        .rd-badge { display:inline-block; padding:4px 10px; border-radius:20px; font-size:11px; font-weight:600; white-space:nowrap; }
        .rd-badge-ok { background:#ecfdf5; color:#059669; border:1px solid #a7f3d0; }
        .rd-badge-pend { background:#fffbeb; color:#d97706; border:1px solid #fcd34d; }

        .rd-view-btn { padding:6px 14px; border:1px solid #e2e8f0; border-radius:8px; background:#f8fafc; color:#2563eb; cursor:pointer; font-size:12px; font-weight:600; font-family:'Inter',sans-serif; transition:all .2s; white-space:nowrap; }
        .rd-view-btn:hover { background:#eff6ff; border-color:#93c5fd; box-shadow:0 2px 8px rgba(37,99,235,.12); transform:translateY(-1px); }

        .rd-empty { padding:40px 20px; text-align:center; color:#94a3b8; font-size:14px; }
        .rd-empty-icon { font-size:40px; margin-bottom:10px; }

        .rd-pag { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
        .rd-pag-info { font-size:13px; color:#64748b; }
        .rd-pag-btns { display:flex; align-items:center; gap:6px; }
        .rd-pag-btns button { padding:8px 14px; border-radius:8px; border:1px solid #e2e8f0; background:#fff; font-size:13px; font-weight:500; color:#334155; cursor:pointer; transition:all .2s; font-family:'Inter',sans-serif; }
        .rd-pag-btns button:hover:not(:disabled) { background:#f1f5f9; }
        .rd-pag-btns button:disabled { opacity:.4; cursor:not-allowed; }
        .rd-pag-btns span { font-size:13px; color:#64748b; padding:0 6px; }

        .rd-load { display:flex; flex-direction:column; align-items:center; padding:60px 20px; gap:14px; }
        .rd-spin { width:36px; height:36px; border:3px solid #e0e7ff; border-top-color:#6366f1; border-radius:50%; animation:rd-sp .7s linear infinite; }
        @keyframes rd-sp { to{transform:rotate(360deg)} }
        .rd-load-t { font-size:14px; color:#64748b; }

        @media(max-width:640px){
          .rd-pag{flex-direction:column;align-items:stretch;text-align:center;}
          .rd-pag-btns{justify-content:center;}
          .rd-summary{flex-direction:column;}
        }
      `}</style>

            <div className="rd-page">
                {/* Header */}
                <div className="rd-hdr">
                    <button className="rd-back" onClick={() => navigate("/dashboard/reports")}>← Back to Reports</button>
                    <div>
                        <div className="rd-title">{meta.icon} {meta.title}</div>
                        <div className="rd-desc">{meta.desc}</div>
                    </div>
                </div>

                {loading ? (
                    <div className="rd-load">
                        <div className="rd-spin" />
                        <div className="rd-load-t">Loading data...</div>
                    </div>
                ) : (
                    <>
                        {/* Summary Cards */}
                        <div className="rd-summary">
                            <div className="rd-summary-card highlight">
                                <div className="rd-summary-val">{fmt(summaryAmount)}</div>
                                <div className="rd-summary-lbl">{meta.title}</div>
                            </div>
                            <div className="rd-summary-card">
                                <div className="rd-summary-val">{tableRows.length}</div>
                                <div className="rd-summary-lbl">Total Records</div>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="rd-search">
                            <input
                                placeholder="🔍 Search by name, unit, project, booking ID..."
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            />
                        </div>

                        {/* Table */}
                        <div className="rd-wrap">
                            <table className="rd-table">
                                <thead>
                                    <tr>
                                        {tableHeaders.map((h) => (
                                            <th key={h}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={tableHeaders.length}>
                                                <div className="rd-empty">
                                                    <div className="rd-empty-icon">📭</div>
                                                    No records found
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedRows.map((row, i) => renderRow(row, i))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="rd-pag">
                                <div className="rd-pag-info">
                                    Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, tableRows.length)} of {tableRows.length}
                                </div>
                                <div className="rd-pag-btns">
                                    <button disabled={page === 1} onClick={() => setPage(1)}>First</button>
                                    <button disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                                    <span>Page {page} of {totalPages}</span>
                                    <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                                    <button disabled={page === totalPages} onClick={() => setPage(totalPages)}>Last</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default ReportDetail;
